// Snapping a viewport to a world-anchored grid, so the viewport routes have a
// cacheable number of distinct URLs.
//
// /cities and /wind are asked for on every `moveend`. The client used to send
// the raw bounds at three decimal places, which means essentially every pan,
// zoom and flyTo produced a URL nobody had ever requested before — so Cloudflare
// could never answer one, and all of it landed on a single 0.25 vCPU origin.
// Worse, the flyTo that a city open performs ends in a `moveend` of its own, so
// opening a city cost a viewport round trip on top of the briefing.
//
// The fix is to ask a coarser question. Both routes answer with everything
// inside a box, and the client already draws only what fits on screen, so an
// answer for a BIGGER box is still a correct answer — MapLibre clips the rest.
// Rounding the box outward to a fixed grid means neighbouring viewports ask the
// identical question, which makes the response cacheable and lets the client
// skip the request entirely when nothing it asks about has changed.
//
// The grid must be anchored to the WORLD, not to the viewport: a grid measured
// from wherever the reader happens to be would move with them, and two
// overlapping views would still never produce the same key.
//
// Pure: no map, no fetch. The client snaps, the server is none the wiser, and
// viewport-snap.test.ts pins the properties that make it safe.

// Degrees the /cities box is rounded to at a given zoom.
//
// An eighth of the visible span, so a small pan reuses the previous key and a
// large one costs at most ~12% more dots per edge than the viewport strictly
// needs. Powers of two divide 360 exactly, which keeps the grid aligned across
// the antimeridian instead of closing on a different meridian going each way.
//
// The floor matters at deep zoom: 360/2**(z+3) collapses toward nothing, and a
// grid finer than the pan the reader can make would go back to a fresh key per
// request. 0.05° (~5 km) is coarse enough to collide and small enough that the
// extra area holds no cities the viewport does not nearly show.
export function cityBboxStep(z: number): number {
  const step = 360 / Math.pow(2, Math.floor(Math.max(0, Math.min(24, z))) + 3);
  return Math.min(45, Math.max(0.05, step));
}

export interface SnappedBox {
  w: number;
  s: number;
  e: number;
  n: number;
}

// Round a bbox OUTWARD to multiples of `step`. Never inward: the answer has to
// cover at least what was asked for, or the reader gets a strip of empty map at
// the edge of every pan.
//
// Longitude is deliberately NOT normalized. A map bounds box legitimately runs
// west 170 / east -170 (across the antimeridian) or west -190 (outside ±180
// entirely) — both are ordinary views with world copies on, and the server's
// lonInRange already reads them on the circle. Flooring west and ceiling east
// independently can only widen such a span, never invert it, so a wrapped box
// stays wrapped and a whole-world box stays whole-world.
export function snapBbox(w: number, s: number, e: number, n: number, step: number): SnappedBox {
  const down = (x: number) => Math.floor(x / step) * step;
  const up = (x: number) => Math.ceil(x / step) * step;
  return {
    w: down(w),
    // Latitude has hard ends, and a request for lat 97 is a question about
    // nothing. Clamping keeps the key tidy without changing any answer.
    s: Math.max(-90, down(Math.min(s, n))),
    e: up(e),
    n: Math.min(90, up(Math.max(s, n))),
  };
}

// The bbox as the query string carries it. Fixed to 4 dp because the steps are
// binary fractions of 360 and float arithmetic leaves 1.4062500000000002 behind
// — which would be a different cache key for the same box.
export function bboxParam({ w, s, e, n }: SnappedBox): string {
  return [w, s, e, n].map((x) => Number(x.toFixed(4))).join(',');
}

// Does the open city have to be asked for BY NAME, or will the plain viewport
// answer already contain it?
//
// /cities takes a `selected=` parameter so an open briefing's dot is returned
// even when it is too small to draw at this zoom — otherwise its green highlight
// ring has no feature to attach to. But that parameter is a cache key no other
// reader shares, so sending it always would leave the single most important
// request — the one the flyTo fires when a city opens — permanently uncacheable.
//
// `mz` is the zoom the city starts drawing at: -1 means "listed only, never
// drawn unselected", and undefined means the caller was not told (a city opened
// from a URL or the search box, where /api/loc does not carry it). A city the
// reader CLICKED was by definition drawn at this zoom, so it needs no help.
// Anything else asks, because a missing ring is a visible bug and a cache miss
// is not.
export function needsSelectedParam(mz: number | undefined, z: number): boolean {
  return !(typeof mz === 'number' && mz >= 0 && mz <= z);
}
